import type { ToolConfig } from "../types";

export const goldTools: ToolConfig[] = [
  // 1. Gold Price Calculator
  {
    slug: "gold-price-calculator",
    category: "gold",
    title: "Gold Price Per Weight Calculator",
    shortTitle: "Gold Price",
    description: "Convert gold prices between rupees per gram and per ounce",
    keywords: ["gold price", "gold weight", "gold cost", "price per gram"],
    inputs: [
      { type: "number", key: "goldWeight", label: "Gold Weight", suffix: "grams", min: 0.1, max: 1000, step: 0.1, default: 10 },
      { type: "number", key: "pricePerGram", label: "Current Gold Price", prefix: "₹/gram", min: 4000, max: 8000, step: 10, default: 6700 },
    ],
    calculate(v) {
      const weight = v.goldWeight as number;
      const price = v.pricePerGram as number;

      const totalPrice = Math.round(weight * price);
      const pricePerTola = Math.round(totalPrice * (11.66 / weight)); // 1 tola = 11.66 grams
      const pricePerOunce = Math.round(totalPrice * (31.1 / weight)); // 1 ounce = 31.1 grams

      return { goldWeight: weight, totalPrice, pricePerTola, pricePerOunce };
    },
    resultFields: [
      { key: "totalPrice", label: "Total Cost", format: "currency", isPrimary: true },
      { key: "goldWeight", label: "Gold Weight", format: "number" },
      { key: "pricePerTola", label: "Price Per Tola", format: "currency" },
      { key: "pricePerOunce", label: "Price Per Ounce", format: "currency" },
    ],
    content: {
      whatIs: "Gold prices in India are quoted in multiple units requiring conversion: price per gram (₹3000-7000 range), price per ounce (31.1 grams), price per tola (11.67 grams traditional Indian unit), and price per 10 grams (standard investment unit). International gold prices are quoted in USD per ounce (typically $1200-2100), requiring INR conversion at current exchange rates plus Indian import duties and bank spreads. Gold price fluctuations are driven by: global interest rates, USD strength, geopolitical tensions, and inflation expectations. Understanding gram/ounce/tola conversions helps calculate investment quantities and compare domestic vs. international prices. Price quotes vary by source: spot prices (live market rates), bank rates (add 1-3% spread), jeweler rates (add 3-8% for making charges). Effective gold investment requires understanding unit conversions and comparing actual landed costs.",
      faqs: [
        { question: "What's the current gold price in India per gram?", answer: "Approximately ₹6,500-7,000/gram (2024). Price fluctuates daily. Check official MCX rates or major jeweler websites for latest prices." },
        { question: "What's the difference between gold per gram and per tola in India?", answer: "1 tola = 11.66 grams. Price per tola ~₹75,000-85,000. Jewelers traditionally quote per tola; modern retail often quotes per gram." },
        { question: "How often does gold price change in India?", answer: "Daily, based on global London Bullion Market Close. MCX (Multi Commodity Exchange) sets standard rates. Prices updated twice daily." },
        { question: "Why does gold price vary between jewelers in India?", answer: "Markup: jewelers add 10-15% to standard rate. Making charges, shop location, retail margins vary. Compare multiple jewelers for best price." },
        { question: "Should I buy gold when price is low in India?", answer: "Gold price long-term stable (0-5% annual appreciation). Buy when needed or as investment for 3-5+ year goal. Timing short-term fluctuations is speculative." },
      ],
      relatedSlugs: ["gold/gold-etf-vs-physical", "gold/gold-making-charges", "gold/gold-investment-return"],
    },
  },

  // 3. Gold Investment Return
  {
    slug: "gold-investment-return",
    category: "gold",
    title: "Gold Investment Return Calculator",
    shortTitle: "Gold Investment Returns",
    description: "Calculate returns on gold investment over various time periods",
    keywords: ["gold investment", "gold return", "gold appreciation", "investment returns"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "annualGrowth", label: "Expected Annual Growth Rate", suffix: "%", min: 0, max: 10, step: 0.5, default: 5 },
      { type: "number", key: "years", label: "Investment Period", suffix: "years", min: 1, max: 30, step: 1, default: 10 },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const growth = (v.annualGrowth as number) / 100;
      const years = v.years as number;

      const futureValue = Math.round(amount * Math.pow(1 + growth, years));
      const totalReturn = futureValue - amount;
      const annualReturn = Math.round(totalReturn / years);

      return { investmentAmount: amount, futureValue, totalReturn, annualReturn };
    },
    resultFields: [
      { key: "futureValue", label: "Future Value", format: "currency", isPrimary: true },
      { key: "investmentAmount", label: "Investment Amount", format: "currency" },
      { key: "totalReturn", label: "Total Return", format: "currency" },
      { key: "annualReturn", label: "Average Annual Return", format: "currency" },
    ],
    content: {
      whatIs: "Gold investment returns historically: 3-6% annually in USD terms, 4-8% in INR terms (includes rupee depreciation). Long-term (10+ years): inflation hedge, preservation of wealth. Short-term (1-3 years): volatile (±15%). Physical gold: slower liquidity; ETFs/digital gold: quick selling. Gold doesn't generate cash flow (dividends), returns purely from price appreciation and rupee depreciation.",
      faqs: [
        { question: "What's the historical return on gold investment in India?", answer: "3-6% annually in USD, 4-8% in INR (includes currency effect). Long-term: wealth preservation, inflation hedge. Beats inflation but lower than stocks (10-12%)." },
        { question: "Is gold good long-term investment in India?", answer: "Yes, for stability and inflation hedge. But returns lag stocks. Ideal portfolio: 10-15% gold (balance risk), rest stocks/bonds. Pure gold plays: poor returns." },
        { question: "Does gold produce income like stocks (dividends) in India?", answer: "No, gold has no cash flow. Only return is from price appreciation and currency effects. Stocks (dividends) and bonds (interest) generate income." },
        { question: "How does inflation affect gold investment in India?", answer: "Gold historically hedges inflation (5-6% rises matched by gold price increase). 10-year gold: preserves purchasing power. Better than savings account (3-4% loses to inflation)." },
        { question: "Should I invest in physical gold or gold ETF in India?", answer: "ETF: cheaper, liquid, no security risk. Physical: tangible, cultural value, premium at jewelers. For investment: ETF better. For occasions: physical gold." },
      ],
      relatedSlugs: ["gold/gold-etf-vs-physical", "gold/gold-price-calculator", "personal-finance/inflation-calculator"],
    },
  },

  // 4. Gold Purity Calculator
  {
    slug: "gold-purity-calculator",
    category: "gold",
    title: "Gold Purity & Price Calculator (24K/22K/18K)",
    shortTitle: "Gold Purity Price",
    description: "Calculate actual gold weight and purity (24K, 22K, 18K)",
    keywords: ["gold purity", "24k", "22k", "18k", "karat", "carat"],
    inputs: [
      { type: "number", key: "weight", label: "Gold Weight", suffix: "grams", min: 0.1, max: 1000, step: 0.1, default: 10 },
      { type: "select", key: "purity", label: "Gold Purity", options: [{ value: "24k", label: "24K (99.9% pure)" }, { value: "22k", label: "22K (91.67% pure)" }, { value: "18k", label: "18K (75% pure)" }], default: "22k" },
      { type: "number", key: "price24K", label: "24K Gold Price", prefix: "₹/gram", min: 5000, max: 8000, step: 50, default: 6700 },
    ],
    calculate(v) {
      const weight = v.weight as number;
      const purity = v.purity as string;
      const price24K = v.price24K as number;

      let purityFactor = 1.0;
      let purityPercent = 100;

      if (purity === "22k") {
        purityFactor = 91.67 / 100;
        purityPercent = 91.67;
      } else if (purity === "18k") {
        purityFactor = 75 / 100;
        purityPercent = 75;
      }

      const pricePerGram = Math.round(price24K * purityFactor);
      const totalPrice = Math.round(weight * pricePerGram);
      const pureGoldContent = Math.round((weight * purityPercent) / 100 * 100) / 100;

      return { weight, purity, pricePerGram, totalPrice, pureGoldContent, purityPercent: Math.round(purityPercent) };
    },
    resultFields: [
      { key: "totalPrice", label: "Total Price", format: "currency", isPrimary: true },
      { key: "pricePerGram", label: "Price Per Gram", format: "currency" },
      { key: "pureGoldContent", label: "Pure Gold Content (grams)", format: "number" },
      { key: "purityPercent", label: "Purity %", format: "percent" },
    ],
    content: {
      whatIs: "Gold purity measured in karats. 24K = 99.9% pure, 22K = 91.67% pure (standard in India), 18K = 75% pure. Price varies by purity: 24K most expensive, 22K standard (jewelry), 18K least (mixed metals). Most Indian jewelry: 22K. Higher purity: more value but softer (harder to shape). Lower purity: harder but less gold content. Always verify purity (BIS hallmark) before buying.",
      faqs: [
        { question: "What gold purity is best to buy in India?", answer: "22K standard for Indian jewelry (good balance of purity and durability). 24K: pure but soft, uncommon jewelry. 18K: Western standard, lower purity." },
        { question: "How much is 22K gold worth compared to 24K?", answer: "22K = 91.67% of 24K price. If 24K ₹6,700/gram, 22K ₹6,141/gram. Price reduction: ~8% due to lower purity." },
        { question: "What's the best purity for gold investment in India?", answer: "24K or 22K (higher purity, better resale value). Avoid low purity. For jewelry: 22K standard. For investment: 24K/999 fineness bars." },
        { question: "How do I verify gold purity in India?", answer: "Look for BIS (Bureau of Indian Standards) hallmark on jewelry. Certificate should specify purity (e.g., 916 for 22K). Lab testing confirms purity." },
        { question: "Does gold purity affect resale value in India?", answer: "Yes, significantly. Higher purity commands premium. 22K jewelry: 80-90% of gold value. Purity loss due to weight reduction and durability differences." },
      ],
      relatedSlugs: ["gold/gold-price-calculator", "gold/gold-making-charges", "gold/hallmark-value"],
    },
  },

  // 5. Gold Making Charges
  {
    slug: "gold-making-charges",
    category: "gold",
    title: "Gold Jewelry Total Cost Calculator",
    shortTitle: "Gold Jewelry Cost",
    description: "Calculate making and labor charges for gold jewelry",
    keywords: ["gold jewelry", "making charges", "jewelry cost", "gold GST"],
    inputs: [
      { type: "number", key: "goldWeight", label: "Gold Weight (22K)", suffix: "grams", min: 1, max: 1000, step: 0.5, default: 20 },
      { type: "number", key: "goldPrice", label: "22K Gold Price", prefix: "₹/gram", min: 5000, max: 7500, step: 50, default: 6141 },
      { type: "number", key: "makingChargePercent", label: "Making Charges", suffix: "%", min: 5, max: 25, step: 1, default: 10 },
      { type: "toggle", key: "includeGST", label: "Include 3% GST", default: true },
    ],
    calculate(v) {
      const weight = v.goldWeight as number;
      const price = v.goldPrice as number;
      const making = (v.makingChargePercent as number) / 100;
      const gst = v.includeGST as boolean;

      const goldCost = weight * price;
      const makingCost = Math.round(goldCost * making);
      const subtotal = goldCost + makingCost;

      let gstAmount = 0;
      if (gst) {
        gstAmount = Math.round(subtotal * 0.03);
      }

      const totalCost = subtotal + gstAmount;

      return { goldCost: Math.round(goldCost), makingCost, subtotal: Math.round(subtotal), gstAmount: Math.round(gstAmount), totalCost: Math.round(totalCost) };
    },
    resultFields: [
      { key: "totalCost", label: "Total Jewelry Cost", format: "currency", isPrimary: true },
      { key: "goldCost", label: "Gold Cost (22K)", format: "currency" },
      { key: "makingCost", label: "Making Charges", format: "currency" },
      { key: "gstAmount", label: "GST (3%)", format: "currency" },
    ],
    content: {
      whatIs: "Gold jewelry cost = gold price + making charges + GST. Gold (22K): per-gram rate. Making charges: 5-15% of gold cost depending on design complexity. Simple designs: 5-8%, intricate: 12-15%. GST: 3% on total. E.g., 20g at ₹6,141/gram + 10% making + 3% GST = total ₹1.48L. Jewelers often quote total price, understand breakdown to spot overcharges.",
      faqs: [
        { question: "What are reasonable making charges for gold jewelry in India?", answer: "5-8%: simple designs. 10-12%: standard designs. 15%+: complex/intricate jewelry. Compare jewelers; charges vary. Negotiate for bulk orders." },
        { question: "Is GST applied on gold jewelry in India?", answer: "Yes, 3% GST on entire jewelry cost (gold + making). Invoice must show GST separately. Some jewelers try to hide GST; always ask." },
        { question: "What should I look for when comparing jewelry prices in India?", answer: "Verify: purity (BIS hallmark), weight (certified scale), making charges rate, GST included/separate. Negotiate making charges (5-10% variation possible)." },
        { question: "Can I negotiate gold jewelry price in India?", answer: "Yes, especially making charges (5-15% range). Negotiate 1-2% of gold cost. Bulk orders: 10-20% discount possible on making." },
        { question: "What's the best time to buy gold jewelry in India?", answer: "Peak seasons (Diwali, weddings): prices higher, heavy discounts available. Off-season: lower prices, less discount. Buy based on need, not timing." },
      ],
      relatedSlugs: ["gold/gold-price-calculator", "gold/gold-purity-calculator", "gold/gold-gst-calculator"],
    },
  },

  // 6. Silver Price Calculator
  {
    slug: "silver-price-calculator",
    category: "gold",
    title: "Silver Price Calculator",
    shortTitle: "Silver Price",
    description: "Convert silver prices and calculate investment returns",
    keywords: ["silver price", "silver weight", "silver cost", "silver investment"],
    inputs: [
      { type: "number", key: "silverWeight", label: "Silver Weight", suffix: "grams", min: 1, max: 10000, step: 1, default: 100 },
      { type: "number", key: "pricePerGram", label: "Current Silver Price", prefix: "₹/gram", min: 50, max: 200, step: 1, default: 85 },
    ],
    calculate(v) {
      const weight = v.silverWeight as number;
      const price = v.pricePerGram as number;

      const totalPrice = weight * price;
      const pricePerOunce = Math.round(price * 31.1);
      const pricePerKilo = Math.round(price * 1000);

      return { silverWeight: weight, totalPrice: Math.round(totalPrice), pricePerOunce, pricePerKilo };
    },
    resultFields: [
      { key: "totalPrice", label: "Total Cost", format: "currency", isPrimary: true },
      { key: "silverWeight", label: "Silver Weight", format: "number" },
      { key: "pricePerOunce", label: "Price Per Ounce", format: "currency" },
      { key: "pricePerKilo", label: "Price Per Kilogram", format: "currency" },
    ],
    content: {
      whatIs: "Silver price in India quoted in ₹/gram. Current price (2024): ₹80-90/gram. Less volatile than gold; used for utensils, jewelry, industrial purposes. Silver to gold price ratio: typically 1:60-80 (1g gold ≈ 60-80g silver cost). Silver jewelry: 92.5% pure standard (sterling silver). Lower investment appeal than gold but good for diversification and jewelry.",
      faqs: [
        { question: "What's the current silver price in India per gram?", answer: "Approximately ₹80-90/gram (2024). Silver much cheaper than gold (1:60-80 ratio). Price fluctuates daily with gold and global demand." },
        { question: "What's the silver to gold price ratio in India?", answer: "Historically 1:60-80. Currently: 1g gold ≈ 70-80g silver cost. Ratio varies; higher ratio makes silver relatively cheaper." },
        { question: "Is silver good investment in India?", answer: "Partly. Good for jewelry and utensils. Investment appeal lower than gold (industrial use volatile). Liquidity: lower than gold. Better for diversification." },
        { question: "What purity is silver jewelry in India?", answer: "925 standard (92.5% pure, rest copper/alloy). Also available: 999 pure (rare, soft), 800 (lower purity). 925 most common for jewelry." },
        { question: "Should I invest in silver coins or bars in India?", answer: "Coins: easier to sell, recognized purity, slight premium. Bars: lower cost per gram, bulk investment. Choice depends on investment size." },
      ],
      relatedSlugs: ["gold/gold-price-calculator", "gold/gold-investment-return", "gold/gold-etf-vs-physical"],
    },
  },

  // 7. Gold ETF vs Physical
  {
    slug: "gold-etf-vs-physical",
    category: "gold",
    title: "Gold ETF vs Physical Gold Comparison",
    shortTitle: "ETF vs Physical",
    description: "Compare costs: gold ETF vs. physical gold investment",
    keywords: ["gold ETF", "gold investment", "gold comparison", "fund vs physical"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1000000 },
      { type: "select", key: "investmentType", label: "Investment Type", options: [{ value: "physical", label: "Physical Gold" }, { value: "etf", label: "Gold ETF" }, { value: "digital", label: "Digital Gold" }], default: "etf" },
      { type: "number", key: "years", label: "Investment Period", suffix: "years", min: 1, max: 30, step: 1, default: 5 },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const type = v.investmentType as string;
      const years = v.years as number;

      // Assume 5% annual return for all (same appreciation)
      const futureValue = Math.round(amount * Math.pow(1.05, years));
      const gain = futureValue - amount;

      let costs = 0;
      let liquidity = "";
      let convenience = "";

      if (type === "physical") {
        // Physical: 2% annual cost (storage, insurance, security), 2% making margin at purchase
        const annualCost = Math.round((amount * 0.02 * years));
        costs = amount * 0.02 + annualCost; // Initial markup + annual storage/insurance
        liquidity = "Low (3-7 days to sell, jeweler visit)";
        convenience = "Need to store, insure, verify purity on sale";
      } else if (type === "etf") {
        // ETF: 0.5-1.5% annual expense ratio
        costs = Math.round(amount * 0.01 * years);
        liquidity = "High (instant selling, T+2 settlement)";
        convenience = "Demat account, quick, digital, no storage";
      } else {
        // Digital: 0.5-1% annual charges
        costs = Math.round(amount * 0.0075 * years);
        liquidity = "High (instant redemption)";
        convenience = "App-based, easy, no physical storage";
      }

      const netGain = gain - costs;

      return { futureValue, gain, costs, netGain, liquidity, convenience };
    },
    resultFields: [
      { key: "futureValue", label: "Expected Value (After 5 Years)", format: "currency", isPrimary: true },
      { key: "netGain", label: "Net Gain (After Costs)", format: "currency" },
      { key: "costs", label: "Total Costs", format: "currency" },
      { key: "liquidity", label: "Liquidity", format: "text" },
    ],
    content: {
      whatIs: "Gold ETF vs physical gold comparison: ETFs (digital) 0.5-1.5% annual cost, instant liquidity, no storage hassle. Physical gold 2-3% annual cost (storage, insurance, verification, making margins), lower liquidity, tangible ownership. For investment: ETF better (lower cost, liquid). For occasions/cultural value: physical better. Hybrid: 70% ETF + 30% physical jewelry balances both benefits.",
      faqs: [
        { question: "Is gold ETF cheaper than physical gold in India?", answer: "Yes, significantly. ETF: 0.5-1.5% annual cost. Physical: 2-3% (storage, insurance, making margin at purchase and sale). ETF wins long-term." },
        { question: "What are the advantages of gold ETF in India?", answer: "Lower cost, instant buy/sell, no storage/security risk, tax-efficient, no purity verification hassle, liquid at market price, easy diversification." },
        { question: "What are advantages of physical gold in India?", answer: "Tangible asset, no market risk, cultural/occasions value, no digital account needed, can use jewelry, no counterparty risk." },
        { question: "How is gold ETF purchased in India?", answer: "Through stock broker/demat account like equity shares. Can buy fractional amounts. Units represent grams of gold. Sell anytime, settle in ₹." },
        { question: "Should I invest in digital gold or gold ETF in India?", answer: "Both similar. Digital gold: easier (app-based), instant. ETF: lower cost (0.5-1%), more tax-efficient. Choice: preference for platform." },
      ],
      relatedSlugs: ["gold/gold-investment-return", "gold/gold-price-calculator", "gold/sovereign-gold-bond"],
    },
  },

  // 8. Sovereign Gold Bond
  {
    slug: "sovereign-gold-bond",
    category: "gold",
    title: "Sovereign Gold Bond Returns Calculator",
    shortTitle: "Sovereign Gold Bond",
    description: "Calculate Sovereign Gold Bond interest and maturity amount",
    keywords: ["SGB", "sovereign gold bond", "gold bond", "bond returns"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 100000, max: 50000000, step: 100000, default: 1000000 },
      { type: "number", key: "bondYears", label: "Bond Tenure", suffix: "years", min: 5, max: 8, step: 1, default: 8 },
      { type: "number", key: "interestRate", label: "Annual Interest Rate", suffix: "%", min: 2, max: 4, step: 0.25, default: 2.5 },
      { type: "number", key: "goldAppreciation", label: "Expected Gold Appreciation", suffix: "%", min: 0, max: 10, step: 1, default: 5 },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const years = v.bondYears as number;
      const interest = (v.interestRate as number) / 100;
      const appreciation = (v.goldAppreciation as number) / 100;

      // Interest income (annual, paid semiannually)
      const totalInterest = Math.round(amount * interest * years);

      // Gold price appreciation on redemption
      const maturityValue = Math.round(amount * Math.pow(1 + appreciation, years));
      const capitalGain = maturityValue - amount;

      // Total return
      const totalReturn = totalInterest + capitalGain;
      const totalMaturityAmount = amount + totalReturn;

      return {
        investmentAmount: amount,
        totalInterest: Math.round(totalInterest),
        capitalGain: Math.round(capitalGain),
        totalReturn: Math.round(totalReturn),
        totalMaturityAmount: Math.round(totalMaturityAmount),
        effectiveReturn: Math.round((totalReturn / amount / years) * 100)
      };
    },
    resultFields: [
      { key: "totalMaturityAmount", label: "Total Maturity Amount", format: "currency", isPrimary: true },
      { key: "totalReturn", label: "Total Return (Interest + Appreciation)", format: "currency" },
      { key: "totalInterest", label: "Interest Income", format: "currency" },
      { key: "capitalGain", label: "Gold Appreciation Gain", format: "currency" },
      { key: "effectiveReturn", label: "Effective Annual Return %", format: "percent" },
    ],
    content: {
      whatIs: "Sovereign Gold Bonds (SGB) issued by RBI: gold price appreciation + 2.5% fixed annual interest. Tenure: 5-8 years. Benefits: no purity verification, storage cost-free (RBI holds), interest tax-free if held till maturity, capital gains tax-efficient (Section 54EC relief available). Minimum ₹500 investment (or 1g gold weight). Issued quarterly. Safe government-backed investment combining gold ownership with interest income.",
      faqs: [
        { question: "What are Sovereign Gold Bonds (SGB) in India?", answer: "Government securities backed by gold. Own equivalent gold amount, earn 2.5% annual interest, benefit from gold price appreciation. RBI holds gold; you get benefits." },
        { question: "What's the return on SGB in India?", answer: "2.5% annual interest + gold price appreciation (0-10% yearly). Total return: 2.5-12.5% p.a. Over 8 years: ₹10L becomes ₹15-20L approximately." },
        { question: "Is SGB interest taxable in India?", answer: "Interest is taxable but deductible under Section 80C. Capital gains tax-free if Section 54EC relief claimed (hold till maturity + specific criteria)." },
        { question: "When are SGB issued in India?", answer: "Issued quarterly by RBI (April, June, August, October series typically). Subscription period: 5-7 days. Check RBI website for next issue date." },
        { question: "Should I buy SGB or physical gold in India?", answer: "SGB better: interest income + appreciation, no storage cost, government-backed safety. Physical: tangible ownership, jewelry use, more accessible. Combination ideal." },
      ],
      relatedSlugs: ["gold/gold-etf-vs-physical", "gold/gold-investment-return", "gold/gold-price-calculator"],
    },
  },

  // 9. Gold GST Calculator
  {
    slug: "gold-gst-calculator",
    category: "gold",
    title: "Gold Purchase GST Calculator",
    shortTitle: "Gold GST",
    description: "Calculate GST and total cost for gold jewelry purchases",
    keywords: ["gold GST", "gold tax", "purchase tax", "GST calculation"],
    inputs: [
      { type: "number", key: "goldCost", label: "Gold Purchase Cost (excluding GST)", prefix: "₹", min: 10000, max: 50000000, step: 10000, default: 1000000 },
      { type: "select", key: "goldType", label: "Gold Type", options: [{ value: "jewelry", label: "Jewelry (3% GST)" }, { value: "coins", label: "Coins (3% GST)" }, { value: "bars", label: "Investment Bars (5% GST)" }], default: "jewelry" },
    ],
    calculate(v) {
      const cost = v.goldCost as number;
      const type = v.goldType as string;

      let gstRate = 0.03;
      if (type === "bars") gstRate = 0.05;

      const gstAmount = Math.round(cost * gstRate);
      const totalCost = cost + gstAmount;

      return { goldCost: cost, gstAmount, gstRate: Math.round(gstRate * 100), totalCost };
    },
    resultFields: [
      { key: "totalCost", label: "Total Cost (Including GST)", format: "currency", isPrimary: true },
      { key: "goldCost", label: "Gold Cost (Before GST)", format: "currency" },
      { key: "gstAmount", label: "GST Amount", format: "currency" },
      { key: "gstRate", label: "GST Rate", format: "percent" },
    ],
    content: {
      whatIs: "GST on gold in India: 3% on jewelry and coins, 5% on investment bars. GST applies to full transaction (gold + making charges). Earlier: 4-6% varying by type; reduced to 3% from 2021. Invoice mandatory for GST applicability. Gold as investment: 5% GST (Ag 3 form for exemption available in some cases). Understanding GST helps verify jeweler's true costs.",
      faqs: [
        { question: "What's the GST rate on gold in India?", answer: "3% on jewelry and coins (standard). 5% on investment bars. Earlier rates 4-6%; reduced to current rates in 2021." },
        { question: "Is GST avoidable on gold purchase in India?", answer: "No, GST applicable on all gold purchases. Invoice must show GST separately. Some exemptions: ancient jewelry sale (used), under certain circumstances." },
        { question: "Who pays GST on gold in India?", answer: "Buyer pays GST. Jeweler collects and deposits to government. Jeweler must provide invoice with GST break-down." },
        { question: "How much did GST on gold reduce jewelry costs in India?", answer: "By ~2-3% when reduced from 4-6% to 3% in 2021. Jewelry became marginally cheaper. Jewelers partially passed benefit to customers." },
        { question: "Can businesses claim GST credit on gold purchase in India?", answer: "Yes, registered businesses. Requires invoice, Ag-3 form for investment gold. Credit claimed as per GST rules. Individuals: no credit available." },
      ],
      relatedSlugs: ["gold/gold-making-charges", "gold/gold-price-calculator", "gold/gold-purity-calculator"],
    },
  },

  // 10. Digital Gold Calculator
  {
    slug: "digital-gold-calculator",
    category: "gold",
    title: "Digital Gold Investment Returns",
    shortTitle: "Digital Gold Returns",
    description: "Calculate returns on digital gold and app-based gold purchase",
    keywords: ["digital gold", "online gold", "gold app", "digital investment"],
    inputs: [
      { type: "number", key: "investmentAmount", label: "Investment Amount", prefix: "₹", min: 100, max: 50000000, step: 100, default: 100000 },
      { type: "number", key: "annualAppreciation", label: "Expected Annual Gold Appreciation", suffix: "%", min: 0, max: 10, step: 1, default: 5 },
      { type: "number", key: "years", label: "Holding Period", suffix: "years", min: 1, max: 30, step: 1, default: 10 },
      { type: "number", key: "annualCharges", label: "Annual Platform Charges", suffix: "%", min: 0.3, max: 1.5, step: 0.1, default: 0.8 },
    ],
    calculate(v) {
      const amount = v.investmentAmount as number;
      const appreciation = ((v.annualAppreciation as number) - (v.annualCharges as number)) / 100;
      const years = v.years as number;

      const maturityAmount = Math.round(amount * Math.pow(1 + appreciation, years));
      const netReturn = maturityAmount - amount;

      return { investmentAmount: amount, maturityAmount, netReturn };
    },
    resultFields: [
      { key: "maturityAmount", label: "Maturity Value", format: "currency", isPrimary: true },
      { key: "investmentAmount", label: "Investment Amount", format: "currency" },
      { key: "netReturn", label: "Net Return (After Charges)", format: "currency" },
    ],
    content: {
      whatIs: "Digital gold allows buying fractional gold through apps (Google Pay, Paytm, PhonePe, etc.). No minimum purchase amount (start with ₹1). Stored by certified custodians. Charges: 0.5-1.5% annually. Buy/sell anytime, settle instantly in bank. Lower cost than physical, highly liquid, no storage hassle. Good for small, frequent investments. Can redeem as physical gold (₹150-500 fee) or cash.",
      faqs: [
        { question: "What are digital gold apps in India?", answer: "Google Pay, Paytm, PhonePe, Amazon Pay, MobiKwik. Buy gold fractionally starting ₹1. Stored by certified custodians like MMTC, GEPL. Physical redemption available." },
        { question: "What are charges for digital gold in India?", answer: "Buying spread: 0.5-1% markup. Annual storage/insurance: 0.5-1%. Selling spread: 0.5-1%. Redemption as physical: ₹150-500. Total annual cost: 0.8-1.5%." },
        { question: "Is digital gold safe in India?", answer: "Yes, stored by certified government/private custodians. Insured against theft/loss. Regulated by SEBI/FEMA. As safe as bank deposits." },
        { question: "How quickly can I sell digital gold in India?", answer: "Instant sell: money in bank account in minutes. No waiting period. Instant liquidity advantage over physical gold (3-7 days)." },
        { question: "Should I buy digital gold or gold ETF in India?", answer: "Digital gold: more accessible, lower minimum (start ₹1), easier redemption. ETF: lower cost (0.5%), tax-efficient, better long-term investment. Digital: for casual/monthly investing." },
      ],
      relatedSlugs: ["gold/gold-etf-vs-physical", "gold/gold-price-calculator", "gold/gold-investment-return"],
    },
  },

  // 11. Gold Import Duty
  {
    slug: "gold-import-duty",
    category: "gold",
    title: "Gold Import Duty Calculator",
    shortTitle: "Gold Import Duty",
    description: "Calculate customs duty and total cost for imported gold jewelry",
    keywords: ["gold import duty", "customs duty", "gold tax", "import cost"],
    inputs: [
      { type: "number", key: "goldQuantity", label: "Gold Quantity", suffix: "grams", min: 1, max: 100000, step: 1, default: 100 },
      { type: "number", key: "internationalRate", label: "International Gold Rate", prefix: "₹/gram", min: 3000, max: 5000, step: 50, default: 4000 },
      { type: "select", key: "importer", label: "Importer Type", options: [{ value: "personal", label: "Personal/Passenger" }, { value: "commercial", label: "Commercial" }], default: "personal" },
    ],
    calculate(v) {
      const quantity = v.goldQuantity as number;
      const rate = v.internationalRate as number;
      const importer = v.importer as string;

      const goldValue = quantity * rate;

      // Import duty: 12.5% for commercial, 10% for personal
      let dutyRate = importer === "personal" ? 0.10 : 0.125;
      const importDuty = Math.round(goldValue * dutyRate);

      // Cess and other taxes (approximate 5% additional)
      const otherTaxes = Math.round((goldValue + importDuty) * 0.05);

      const totalCost = goldValue + importDuty + otherTaxes;

      return { goldValue: Math.round(goldValue), importDuty, otherTaxes, totalCost: Math.round(totalCost) };
    },
    resultFields: [
      { key: "totalCost", label: "Total Cost (Gold + Duty + Tax)", format: "currency", isPrimary: true },
      { key: "goldValue", label: "Gold Value", format: "currency" },
      { key: "importDuty", label: "Import Duty", format: "currency" },
      { key: "otherTaxes", label: "Other Taxes & Cess", format: "currency" },
    ],
    content: {
      whatIs: "Gold import duty in India: 10-12.5% depending on importer type (personal/commercial). Plus cess, GST, and handling charges, total cost 20-25% above international gold price. Made gold in India cheaper after jewelry markup than imported. Smuggling risk: 10-20% of imported gold smuggled illegally to avoid duty. Policy goal: encourage domestic gold jewelry making, reduce import dependence.",
      faqs: [
        { question: "What's the import duty on gold in India?", answer: "10% for personal travelers, 12.5% for commercial imports. Plus 5% cess, GST, handling fees. Total: 20-25% additional cost on gold." },
        { question: "How much customs duty on gold jewelry in India?", answer: "Finished jewelry: 10-12.5% duty (same as bullion). Implies: buying jewelry abroad and importing expensive. Made in India cheaper." },
        { question: "Is there an exemption limit for gold imports in India?", answer: "Travelers: 25g per person duty-free (if gold articles, higher limit). Commercial: no exemption. Non-resident Indians (NRIs): relaxed limits up to 500g." },
        { question: "Why is gold duty so high in India?", answer: "Policy: encourage domestic gold jewelry making industry, reduce import bill (gold major import item), revenue generation. High duty increases made gold competitiveness." },
        { question: "Is smuggling gold into India a problem?", answer: "Yes, significant. 10-20% estimated gold smuggling due to high duty. Smuggled gold available cheaper but no legal recourse if quality issues." },
      ],
      relatedSlugs: ["gold/gold-price-calculator", "gold/gold-purity-calculator", "gold/gold-making-charges"],
    },
  },

  // 12. Gold Savings Scheme
  {
    slug: "gold-savings-scheme",
    category: "gold",
    title: "Monthly Gold Savings Plan Calculator",
    shortTitle: "Gold Savings Scheme",
    description: "Calculate returns on government gold savings schemes",
    keywords: ["gold savings", "monthly gold", "systematic gold purchase", "gold scheme"],
    inputs: [
      { type: "number", key: "monthlyAmount", label: "Monthly Investment", prefix: "₹", min: 1000, max: 1000000, step: 1000, default: 10000 },
      { type: "number", key: "months", label: "Investment Period", suffix: "months", min: 3, max: 120, step: 3, default: 60 },
      { type: "number", key: "goldPrice", label: "Current Gold Price", prefix: "₹/gram", min: 5000, max: 8000, step: 50, default: 6700 },
      { type: "number", key: "annualAppreciation", label: "Annual Gold Appreciation", suffix: "%", min: 0, max: 10, step: 1, default: 5 },
    ],
    calculate(v) {
      const monthly = v.monthlyAmount as number;
      const months = v.months as number;
      const goldPrice = v.goldPrice as number;
      const appreciation = (v.annualAppreciation as number) / 100;

      // Average price (assuming linear appreciation)
      const startPrice = goldPrice / Math.pow(1 + appreciation, months / 12 / 2);
      let totalGoldGrams = 0;
      let totalInvested = monthly * months;

      for (let i = 0; i < months; i++) {
        const monthPrice = startPrice * Math.pow(1 + appreciation, (i + 1) / 12);
        totalGoldGrams += monthly / monthPrice;
      }

      const currentValue = Math.round(totalGoldGrams * goldPrice);
      const gain = currentValue - totalInvested;

      return { totalInvested, totalGoldGrams: Math.round(totalGoldGrams * 100) / 100, currentValue, gain };
    },
    resultFields: [
      { key: "currentValue", label: "Current Gold Value", format: "currency", isPrimary: true },
      { key: "totalGoldGrams", label: "Total Gold Accumulated", format: "number" },
      { key: "totalInvested", label: "Total Invested", format: "currency" },
      { key: "gain", label: "Gains from Appreciation", format: "currency" },
    ],
    content: {
      whatIs: "Monthly gold savings plan: systematic purchase of gold every month (₹1,000-1L per month). Benefits: rupee-cost averaging (buy more grams when price low, fewer when high), disciplined investing, hedge against inflation. Suited for: irregular savers, those wanting physical gold monthly, wedding/jewel accumulation. Many jewelers offer zero-interest installment plans (gold locked, delivered after payment). Average: 2-5 grams monthly at ₹10,000 investment.",
      faqs: [
        { question: "How much gold can I accumulate with monthly savings in India?", answer: "₹10k/month at ₹6,700/gram = 1.5g monthly, 18g yearly. Over 5 years: ~90g at ₹600k cost. Appreciation adds value." },
        { question: "What are gold savings schemes available in banks in India?", answer: "Most banks: no exclusive schemes. But offer standing instructions for ETF/sovereign bonds. Jewelers: installment plans (lock gold, pay monthly, deliver after). Online apps: digital gold SIPs." },
        { question: "Is rupee-cost averaging effective for gold investment in India?", answer: "Yes, buying consistent amounts reduces average cost. You buy more grams when price low, fewer when high. Long-term advantage." },
        { question: "Can I get interest on gold savings in India?", answer: "No interest on physical gold savings. But SGB (2.5% interest) or gold ETF SIP provides return. Pure gold accumulation: no interest." },
        { question: "What are jeweler gold installment plans in India?", answer: "Monthly payment ₹X for X grams locked. Delivered after full payment. No interest, but gold price locked. Protects from price surge but misses appreciation if prices fall." },
      ],
      relatedSlugs: ["gold/sovereign-gold-bond", "gold/digital-gold-calculator", "gold/gold-investment-return"],
    },
  },

  // 13. Gold Hallmark Value
  {
    slug: "gold-hallmark-value",
    category: "gold",
    title: "BIS Hallmark Gold Value Calculator",
    shortTitle: "Gold Hallmark Value",
    description: "Calculate value based on BIS hallmark purity verification",
    keywords: ["hallmark", "BIS hallmark", "gold hallmark", "purity mark"],
    inputs: [
      { type: "number", key: "jewelWeight", label: "Jewelry Weight", suffix: "grams", min: 1, max: 1000, step: 0.5, default: 50 },
      { type: "select", key: "hallmarkCode", label: "BIS Hallmark Code", options: [{ value: "999", label: "999 (99.9% pure)" }, { value: "995", label: "995 (99.5% pure)" }, { value: "916", label: "916 (91.6% = 22K)" }, { value: "875", label: "875 (87.5% = 21K)" }, { value: "750", label: "750 (75% = 18K)" }], default: "916" },
      { type: "number", key: "goldRate", label: "Current Gold Rate (Per gram 24K)", prefix: "₹", min: 5000, max: 8000, step: 50, default: 6700 },
    ],
    calculate(v) {
      const weight = v.jewelWeight as number;
      const hallmark = v.hallmarkCode as string;
      const rate24K = v.goldRate as number;

      let purityPercent = 0;
      if (hallmark === "999") purityPercent = 99.9;
      else if (hallmark === "995") purityPercent = 99.5;
      else if (hallmark === "916") purityPercent = 91.6;
      else if (hallmark === "875") purityPercent = 87.5;
      else purityPercent = 75;

      const pureGoldContent = (weight * purityPercent) / 100;
      const goldValue = Math.round(pureGoldContent * rate24K);

      return { jewelWeight: weight, hallmarkCode: hallmark, purityPercent, pureGoldContent: Math.round(pureGoldContent * 100) / 100, goldValue };
    },
    resultFields: [
      { key: "goldValue", label: "Gold Value", format: "currency", isPrimary: true },
      { key: "pureGoldContent", label: "Pure Gold Content (grams)", format: "number" },
      { key: "purityPercent", label: "Purity %", format: "percent" },
    ],
    content: {
      whatIs: "BIS (Bureau of Indian Standards) hallmark certifies gold purity. Codes: 999 (99.9% pure), 916 (91.6% = 22K standard), 750 (75% = 18K). Hallmark essential for jewelry resale credibility, loan value, insurance. Hallmarked jewelry commands 5-10% premium at resale vs non-hallmarked (purity doubts). Always verify hallmark before buying significant jewelry. Marks: fineness number + hallmark symbol + assay office symbol.",
      faqs: [
        { question: "What does BIS hallmark number mean on gold jewelry in India?", answer: "Fineness code: 999 (99.9% pure), 916 (91.6% = 22K), 750 (75% = 18K). Higher number = higher purity = higher value." },
        { question: "Is hallmarked gold more valuable in India?", answer: "Yes, 5-10% premium at resale due to certified purity. Lenders give higher gold loan on hallmarked. Insurance claims easier with hallmark." },
        { question: "Can all gold jewelry be hallmarked in India?", answer: "Yes, voluntary but recommended. BIS assay centers hallmark for fee (₹50-200 depending on weight). Certificate provided with hallmark." },
        { question: "How to verify BIS hallmark on gold in India?", answer: "Look for: hallmark symbol (crown with assay office mark), fineness number (916, 999), assay office abbreviation (ALBD, BLRD, etc.), year mark (last 2 digits of year)." },
        { question: "Does hallmark add to gold jewelry cost in India?", answer: "Hallmarking fee: ₹50-200 per piece. Minimal vs jewelry cost. Premium value gained (5-10% at resale) far exceeds hallmarking cost." },
      ],
      relatedSlugs: ["gold/gold-purity-calculator", "gold/gold-making-charges", "gold/gold-price-calculator"],
    },
  },

  // 14. Gold Weight Converter
  {
    slug: "gold-weight-converter",
    category: "gold",
    title: "Gold Weight Unit Converter",
    shortTitle: "Weight Converter",
    description: "Convert gold weight between grams, tolas, and ounces",
    keywords: ["weight converter", "tola to gram", "ounce to gram", "gold weight"],
    inputs: [
      { type: "number", key: "grams", label: "Weight (Grams)", suffix: "grams", min: 0, max: 100000, step: 0.1, default: 100 },
    ],
    calculate(v) {
      const grams = v.grams as number;

      const tola = Math.round((grams / 11.66) * 100) / 100;
      const ounce = Math.round((grams / 31.1) * 100) / 100;
      const kilogram = Math.round((grams / 1000) * 10000) / 10000;
      const milligrams = grams * 1000;

      return { grams, tola, ounce, kilogram, milligrams };
    },
    resultFields: [
      { key: "grams", label: "Grams", format: "number", isPrimary: true },
      { key: "tola", label: "Tola (Indian unit)", format: "number" },
      { key: "ounce", label: "Ounce (Troy)", format: "number" },
      { key: "kilogram", label: "Kilogram", format: "number" },
      { key: "milligrams", label: "Milligrams", format: "number" },
    ],
    content: {
      whatIs: "Gold weight units in India: grams (metric, standard), tola (traditional = 11.66g), ounce/troy ounce (international = 31.1g), kilogram. Conversions: 1 tola ≈ 11.66g, 1 ounce ≈ 31.1g, 1kg = 1000g. Jewelers traditionally quote tola; modern retail quotes grams. Understanding conversions avoids overpaying due to unit confusion.",
      faqs: [
        { question: "What's the conversion from tola to grams in India?", answer: "1 tola = 11.66 grams. Traditional unit still used by jewelers. 1g ≈ 0.086 tola." },
        { question: "How many grams is 1 troy ounce of gold?", answer: "1 troy ounce = 31.1 grams. International standard for precious metals. Gold prices internationally quoted per troy ounce." },
        { question: "Why are different weight units used for gold in India?", answer: "Historical: tola traditional Indian unit. Modern: grams (metric, easy conversion). International: troy ounce (standard in global markets)." },
        { question: "Should I ask jeweler for weight in grams or tola in India?", answer: "Grams preferred (metric, easy calculation). But many jewelers use tola traditionally. Ask for both for clarity." },
        { question: "How many grams of gold should I have for insurance in India?", answer: "Depends on insurance value. Typical: ₹1 lakh ≈ 15g (at ₹6,700/gram). Keep weight record for insurance claims." },
      ],
      relatedSlugs: ["gold/gold-price-calculator", "gold/gold-purity-calculator", "gold/gold-making-charges"],
    },
  },

  // 15. Gold Loan Eligibility
  {
    slug: "gold-loan-eligibility",
    category: "gold",
    title: "Gold Loan Maximum Amount Calculator",
    shortTitle: "Gold Loan Eligibility",
    description: "Check maximum gold loan amount based on weight and purity",
    keywords: ["gold loan", "loan eligibility", "loan amount", "gold value"],
    inputs: [
      { type: "number", key: "ornamentWeight", label: "Gold Ornament Weight", suffix: "grams", min: 1, max: 10000, step: 0.5, default: 100 },
      { type: "select", key: "ornamentPurity", label: "Ornament Purity", options: [{ value: "24k", label: "24K (99.9% pure)" }, { value: "22k", label: "22K (91.67% pure)" }, { value: "18k", label: "18K (75% pure)" }], default: "22k" },
      { type: "number", key: "currentGoldRate", label: "Current Gold Rate", prefix: "₹/gram", min: 5000, max: 8000, step: 50, default: 6700 },
      { type: "number", key: "loanPercent", label: "Loan-to-Value (LTV)", suffix: "%", min: 50, max: 85, step: 5, default: 75 },
    ],
    calculate(v) {
      const weight = v.ornamentWeight as number;
      const purity = v.ornamentPurity as string;
      const rate = v.currentGoldRate as number;
      const ltv = (v.loanPercent as number) / 100;

      let purityPercent = 1.0;
      if (purity === "22k") purityPercent = 0.9167;
      else if (purity === "18k") purityPercent = 0.75;

      const goldValue = Math.round(weight * purityPercent * rate);
      const maxLoan = Math.round(goldValue * ltv);

      return { ornamentWeight: weight, goldValue, maxLoan, loanPercent: Math.round(ltv * 100) };
    },
    resultFields: [
      { key: "maxLoan", label: "Maximum Gold Loan Amount", format: "currency", isPrimary: true },
      { key: "goldValue", label: "Gold Ornament Value", format: "currency" },
      { key: "loanPercent", label: "LTV Applied", format: "percent" },
    ],
    content: {
      whatIs: "Gold loan eligibility: 70-75% LTV (loan-to-value) typical. For 100g 22K gold at ₹6,700/gram: value ₹6.1L, loan ₹4.3-4.6L. Factors: gold purity (24K: higher loan; 18K: lower), current gold rate (fluctuates daily), lender's LTV policy. Quick approval (same day), no credit check, relatively hassle-free compared to traditional loans.",
      faqs: [
        { question: "How much gold loan can I get for 100 grams of gold in India?", answer: "100g 22K at ₹6,700/gram (value ₹6.1L) → loan ₹4.3-4.6L at 75% LTV. Varies by lender and gold purity." },
        { question: "What's LTV in gold loans in India?", answer: "Loan-to-Value: percentage of gold value the lender gives as loan. 75% LTV means: ₹1L gold → ₹75k loan. Protects lender in price fluctuation." },
        { question: "What gold ornaments qualify for gold loan in India?", answer: "Any: jewelry, bars, coins, religious items (except some). Purity verified; lower purity reduces loan amount. 24K gets best LTV." },
        { question: "Can I get gold loan on pledged jewelry in India?", answer: "Yes, most common. Jewelry pledged to lender for loan duration. After repayment, jewelry released. If default: jewelry auctioned." },
        { question: "How does gold price fluctuation affect gold loan in India?", answer: "If price drops after loan: no impact (you already got loan at original rate). If you need more loan: new amount based on current lower price means less eligible." },
      ],
      relatedSlugs: ["gold/gold-loan-emi", "gold/gold-price-calculator", "gold/gold-purity-calculator"],
    },
  },
];
